"use client";

import type { ReactNode } from "react";

import React from "react";
import Link from "next/link";
import ServiceTemplatePage from "@components/services/ServiceTemplatePage";
import SectionHead from "@components/services/SectionHead";
import AnswerCard from "@components/services/AnswerCard";
import KeyFactsBar from "@components/services/KeyFactsBar";
import ScopeGrid from "@components/services/ScopeGrid";
import WhyDbiCards from "@components/services/WhyDbiCards";
import ProcessSteps from "@components/services/ProcessSteps";
import PricingTiers from "@components/services/PricingTiers";
import CaseStudyCard from "@components/services/CaseStudyCard";
import type { HeroData } from "@components/services/types";

/** Icons `ScopeGrid` can render for the Ausgangslage cards. */
type ScopeIconName =
  | "Search"
  | "Bot"
  | "FileText"
  | "Network"
  | "Database"
  | "Compass"
  | "RefreshCcw"
  | "Lightbulb";

interface CtaLink {
  label: string;
  href: string;
}

/**
 * Data contract of a consulting page: Einführung → Ausgangslage → Optionen →
 * Schritte → Referenz → FAQ. The JSON for each page lives in
 * `src/data/landing-pages/<route>.json`; nothing here is page-specific copy.
 */
export interface ConsultingPageData {
  hero: HeroData;
  /** 01 · Einführung — what the subject is and when it pays off. */
  intro: {
    eyebrow: string;
    title: string;
    paragraphs: string[];
    keyfacts: { label: string; sublabel: string }[];
  };
  /** 02 · Ausgangslage — the typical starting situations we meet. */
  situation: {
    eyebrow: string;
    title: string;
    lede: string;
    items: { iconName: ScopeIconName; title: string; description: string }[];
  };
  /** 03 · Optionen — the routes a client can take and how we pick one. */
  options: {
    eyebrow: string;
    title: string;
    lede: string;
    intro: string;
    image: string;
    imageAlt: string;
    cards: { id: string; icon: string; title: string; description: string; note: string }[];
  };
  /** 04 · Schritte — the engagement, step by step, and what follows it. */
  process: {
    eyebrow: string;
    title: string;
    badge: string;
    steps: { number: number; title: string; description: string }[];
    /** Link rendered inside step 01, where the questionnaire is described. */
    questionnaireCta: CtaLink;
    pricing: {
      heading: string;
      layout: "grid" | "rows";
      tiers: {
        name: string;
        price: string;
        priceSuffix?: string;
        sub: string;
        features: string[];
        featured?: boolean;
        cta?: CtaLink;
      }[];
    };
  };
  /** 05 · Referenz — one anonymised case. */
  reference: {
    eyebrow: string;
    title: string;
    lede: string;
    caseStudy: {
      tag: string;
      title: string;
      quote: string;
      image: string;
      imageAlt: string;
      metrics: { value: string; label: string }[];
      cta: CtaLink;
    };
  };
  faq: {
    eyebrow: string;
    title: string;
    faqs: { question: string; answer: string }[];
  };
  /** Order of the sections on the page and in the TOC. `section` keys must
   *  be one of: intro, situation, options, process, reference, faq. */
  serviceSubnav: { id: string; label: string; section: string }[];
  relatedServices: {
    tag: string;
    title: string;
    description: string;
    href: string;
  }[];
  ctaBlock: {
    title: string;
    description: string;
    primaryCta: CtaLink;
    secondaryCta?: CtaLink;
  };
}

interface ConsultingServiceClientProps {
  data: ConsultingPageData;
  /** GuideReadNext for this route, rendered by the server page. */
  guideSlot: ReactNode;
  /** ServiceContactCard for this route, rendered by the server page. */
  contactSlot: ReactNode;
}

const REQUIRED_SECTIONS = [
  "hero",
  "intro",
  "situation",
  "options",
  "process",
  "reference",
  "faq",
  "serviceSubnav",
  "relatedServices",
  "ctaBlock",
] as const;

function assertData(data: ConsultingPageData) {
  for (const key of REQUIRED_SECTIONS) {
    if (!data[key]) {
      throw new Error(`ConsultingServiceClient: missing \`${key}\` in page JSON.`);
    }
  }
}

/**
 * ConsultingServiceClient — the consultant's page, not the engineer's.
 *
 * Five sections read in the order a first conversation would: what the
 * subject is, where the visitor stands today, which routes exist, how the
 * engagement runs, and one reference. Each section is one heading plus one
 * module, so the eye has a single thing to settle on. Used by the
 * Microsoft Fabric Beratung and the Power BI Migration pages; both share
 * this layout so they cannot drift apart.
 */
export default function ConsultingServiceClient({
  data,
  guideSlot,
  contactSlot,
}: ConsultingServiceClientProps) {
  assertData(data);

  const sectionByKey: Record<string, React.ReactNode> = {
    intro: (
      <>
        <SectionHead eyebrow={data.intro.eyebrow} title={data.intro.title} blue />
        <AnswerCard paragraphs={data.intro.paragraphs} />
        <KeyFactsBar facts={data.intro.keyfacts} />
      </>
    ),
    situation: (
      <>
        <SectionHead
          eyebrow={data.situation.eyebrow}
          title={data.situation.title}
          lede={data.situation.lede}
          blue
        />
        <ScopeGrid items={data.situation.items} />
      </>
    ),
    options: (
      <>
        <SectionHead
          eyebrow={data.options.eyebrow}
          title={data.options.title}
          lede={data.options.lede}
          blue
        />
        <WhyDbiCards
          cards={data.options.cards}
          image={data.options.image}
          imageAlt={data.options.imageAlt}
          intro={data.options.intro}
        />
      </>
    ),
    process: (
      <>
        <SectionHead eyebrow={data.process.eyebrow} title={data.process.title} blue />
        <ProcessSteps
          steps={data.process.steps}
          badge={data.process.badge}
          stepAddons={{
            1: (
              <Link className="process-step__cta" href={data.process.questionnaireCta.href}>
                {data.process.questionnaireCta.label}
                <span aria-hidden="true">→</span>
              </Link>
            ),
          }}
        />
        <PricingTiers
          heading={data.process.pricing.heading}
          layout={data.process.pricing.layout}
          tiers={data.process.pricing.tiers}
        />
      </>
    ),
    reference: (
      <>
        <SectionHead
          eyebrow={data.reference.eyebrow}
          title={data.reference.title}
          lede={data.reference.lede}
          blue
        />
        <CaseStudyCard
          tag={data.reference.caseStudy.tag}
          title={data.reference.caseStudy.title}
          quote={data.reference.caseStudy.quote}
          image={data.reference.caseStudy.image}
          imageAlt={data.reference.caseStudy.imageAlt}
          metrics={data.reference.caseStudy.metrics}
          cta={data.reference.caseStudy.cta}
        />
      </>
    ),
  };

  const sections = data.serviceSubnav
    .filter((item) => item.section !== "faq")
    .map((item) => {
      const content = sectionByKey[item.section];
      if (!content) {
        throw new Error(
          `ConsultingServiceClient: unsupported section key '${item.section}' in serviceSubnav.`,
        );
      }
      return { id: item.id, content };
    });

  return (
    <ServiceTemplatePage
      hero={data.hero}
      subnav={data.serviceSubnav.map((item) => ({ id: item.id, label: item.label }))}
      sections={sections}
      faqEyebrow={data.faq.eyebrow}
      faqTitle={data.faq.title}
      faqs={data.faq.faqs}
      relatedServices={data.relatedServices}
      guideSlot={guideSlot}
      contactSlot={contactSlot}
      ctaBlock={data.ctaBlock}
    />
  );
}
